import { useMsal } from '@azure/msal-react'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { LOCAL_STORAGE_KEYS, USER_ROLE } from '@src/utils/constants.ts'
import { useAppContext } from './AppContext'
import { CurrentUserPolls, PollModalB, PollModalF } from '@src/pages/Poll/Components/PollComponentType'
import { isNilOrEmpty } from '@src/utils/isNilOrEmpty'

interface UserData {
  currentUser: User | null
  isUserLoading: boolean
  currentUserPolls: CurrentUserPolls
  refreshUser: () => Promise<void>  
}

export interface User {
  id: string
  name: string
  userRole: string
  accountName: string
  accountId: string
  email: string
  isParent: boolean
  accessInfo: string[]
  roles: string[]
  userJourney: string
}

export const mapServerPollsToClient = (
  data: Record<string, PollModalB> | undefined
): CurrentUserPolls | null => {
  if (isNilOrEmpty(data)) {
    return null
  }

  const mapped: CurrentUserPolls = {}
  Object.entries(data!).forEach(([key, poll]) => {
    mapped[key] = {
      pollTemplateId: poll.poll_template_id,
      requirementLevel: poll.requirement_level,
      allowedUsers: poll.allowed_users ?? [],
      targetDevices: poll.target_devices ?? [],
      status: poll.status ?? '',
      lastQuestion: poll.last_question ?? '',
      lastUpdate: poll.last_update ?? '',
    }
  })

  return mapped
}

const UserContext = createContext<UserData | null>(null)

export const useUserContext = () => {
  return useContext(UserContext)
}

export const UserProvider = ({ children, dataProvider, userId }: any) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [isUserLoading, setIsUserLoading] = useState(true)
  const [currentUserPolls, setCurrentUserPolls] = useState<CurrentUserPolls>({})
  const isFetching = useRef(false)
  const { instance } = useMsal()
  const { setAppKey } = useAppContext()

  const handleLogout = () => {
    // userId, AccoutnId - will set during useTokenManagement context - Home component
    // msalClientId, msalClientUrl, apiUrl- will be set in login page
    // msalB2CId - will be set after login - App component
    LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))

    // issue in msal, sometimes this below status is not cleared so manually clearing the msal.interaction.status
    sessionStorage.removeItem('msal.interaction.status')
    localStorage.setItem('user_logged_out', Date.now().toString()) // for cross-tab logout handling
    instance.logoutRedirect()
  }

  const fetchData = useCallback(async () => {
    if (isFetching.current) return
    try {
      isFetching.current = true
      setIsUserLoading(true) 
      if (dataProvider && userId) {
        const response = await dataProvider.getOne('User', {
          id: userId,
          method: 'getUserData',
        })

        if (response.data) {
          const data = response.data

          const userDataSubset: User = {
            id: data.id,
            name: `${data.first_name} ${data.last_name}`,
            userRole: data.user_role,
            roles: data.roles,
            accountName: data.account_name,
            accountId: data.account_id,
            email: data.primary_email,
            isParent: data.roles.includes(USER_ROLE.PARENT),
            accessInfo: data.access_info,
            userJourney: data.user_journey?.stage ?? '',
            ...(data.account_settings ? { viewAccSetting: 'true' } : { viewAccSetting: 'false' }),
          }

          setCurrentUser(userDataSubset)
          setCurrentUserPolls(mapServerPollsToClient(data.assigned_polls) ?? {})
          setAppKey('accSettings', data.account_settings)
          setAppKey('accountName', data.account_name)
        } else {
          handleLogout()
        }
      }
      setIsUserLoading(false)
    } catch (error: any) {
      if (error instanceof Error && error.message.includes('not active')) {
        handleLogout()
      }
    } finally {
      isFetching.current = false
    }
  }, [dataProvider, userId, setAppKey, instance])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return <UserContext.Provider value={{ currentUser, isUserLoading, currentUserPolls, refreshUser: fetchData }}>{children}</UserContext.Provider>
}
